/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import { Carousel, ICarouselProps, Player, IPlayerData, IPlayerOptions, IVideoMetadata } from '@msdyn365-commerce/components';
import { IImageData, IImageSettings, Image, Video } from '@msdyn365-commerce/core';
import { MediaLocation, MediaType, SimpleProduct } from '@msdyn365-commerce/retail-proxy';
import { ArrayExtensions, getFallbackImageUrl, ObjectExtensions } from '@msdyn365-commerce-modules/retail-actions';
import {
    defaultDataScale,
    IComponentNodeProps,
    ImagefullView,
    IModalViewProps,
    IModuleProps,
    inlineZoomImageOnHover,
    inlineZoomImageOnMouseMove,
    inlineZoomImageOnMouseOut,
    inlineZoomInitClick,
    INodeProps,
    ISingleSlideCarouselProps,
    isMobile,
    KeyCodes,
    NodeTag,
    onContainerZoomInit,
    onMouseMoveLensContainer,
    onMouseOutLensContainer,
    onMouseOverImageContainer,
    removeContainerZoomStyle,
    removeInlineZoomStyle,
    SingleSlideCarousel,
    VariantType
} from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import { reaction } from 'mobx';
import * as React from 'react';

import { IBritaxMediaGalleryData } from './britax-media-gallery.data';
import { imageSource, imageZoom, IBritaxMediaGalleryProps, thumbnailsOrientation } from './britax-media-gallery.props.autogenerated';
import { getProductImages, getValidProductImages, getVideoMetadataFromApi, validateProductImages } from './utils';
import { getAltText, IMediaData } from './utils/helper';

/**
 * IMediaGalleryState Interface.
 */
export interface IMediaGalleryState {
    animating: boolean;
    activeIndex: number;
    isImageZoomed: boolean;
    modalIsOpen: boolean;
    lastUpdate?: number;
    isMobileImageZoomed?: boolean;
    mediaGalleryItems?: IMediaData[];
    isInitialLoad?: boolean;
    shouldUseOptimizedImage?: boolean;
}

/**
 * IMediaGalleryThumbnailsViewProps Interface.
 */
export interface IMediaGalleryThumbnailsViewProps {
    ThumbnailsContainerProps: INodeProps;
    SingleSlideCarouselComponentProps: INodeProps;
    items?: IMediaGalleryThumbnailItemViewProps[];
}

/**
 * IMediaGalleryThumbnailItemViewProps Interface.
 */
export interface IMediaGalleryThumbnailItemViewProps {
    ThumbnailItemContainerProps: INodeProps;
    Picture: React.ReactElement;
}

/**
 * IMediaGalleryViewProps Interface.
 */
export interface IMediaGalleryViewProps extends IBritaxMediaGalleryProps<IBritaxMediaGalleryData> {
    state: IMediaGalleryState;
    MediaGallery: IModuleProps;
    CarouselProps: INodeProps;
    Thumbnails: IMediaGalleryThumbnailsViewProps;
    Modal?: React.ReactElement | null;
    callbackToggle?(): void;
    callbackThumbnailClick?(index: number): void;
    callbackThumbnailKeyDown?(index: number): void;
}

/**
 * Props for carousel.
 */
interface IMediaGalleryCarouselItems<ItemType> {
    items: ItemType[];
    keys: (string | undefined)[];
}

/**
 * Media gallery component.
 */
class MediaGallery extends React.Component<IBritaxMediaGalleryProps<IBritaxMediaGalleryData>, IMediaGalleryState> {
    private readonly _inlineZoomDivRef: Map<number, HTMLDivElement> = new Map();

    private readonly fullScreenOverlayButtonRef: React.RefObject<HTMLAnchorElement & HTMLButtonElement> = React.createRef();
    private readonly player: React.RefObject<Player> = React.createRef<Player>();

    private mediaGalleryImages: IImageData[] = [];

    private fallbackImage?: string;

    private readonly defaultGalleryImageSettings: IImageSettings = {
        viewports: {
            xs: { q: 'w=767&h=767&m=8', w: 0, h: 0 },
            sm: { q: 'w=600&h=600&m=8', w: 0, h: 0 },
            md: { q: 'w=600&h=772&m=8', w: 0, h: 0 },
            lg: { q: 'h=772&m=8', w: 0, h: 0 }
        },
        lazyload: true,
        cropFocalRegion: true
    };

    private readonly defaultThumbnailImageSettings: IImageSettings = {
        viewports: {
            xs: { q: 'w=100&m=8', w: 100, h: 0 },
            lg: { q: 'w=100&m=8', w: 100, h: 0 }
        },
        lazyload: true,
        cropFocalRegion: true
    };

    private readonly _zoomedImageSettings: IImageSettings;

    public constructor(props: IBritaxMediaGalleryProps<IBritaxMediaGalleryData>) {
        super(props);
        this._toggleModal = this._toggleModal.bind(this);
        this.state = {
            activeIndex: 0,
            animating: false,
            isImageZoomed: false,
            modalIsOpen: false,
            isInitialLoad: true,
            shouldUseOptimizedImage: !!this.props.config.skipImageValidation
        };

        this._zoomedImageSettings =
            props.config.zoomedImageSettings ?? props.config.galleryImageSettings ?? this.defaultGalleryImageSettings;

        this._onImageMouseOut = this._onImageMouseOut.bind(this);
        this._inlineZoomImageOnHover = this._inlineZoomImageOnHover.bind(this);
        this._onInlineImageClick = this._onInlineImageClick.bind(this);
        this._onContainerZoomImageClick = this._onContainerZoomImageClick.bind(this);
        this._onMouseOutLensContainer = this._onMouseOutLensContainer.bind(this);
    }

    public isMobile(): boolean {
        const size = isMobile({ variant: VariantType.Viewport, context: this.props.context.request });
        return size === 'xs';
    }

    public async componentDidMount(): Promise<void> {
        const source = this.props.config.imageSource ?? imageSource.pageContext;
        const shouldUpdateOnPartialDimensionSelection = this.props.config.shouldUpdateOnPartialDimensionSelection;
        this._setRenderType();
        if (!this.props.context.request.app?.config?.OmniChannelMedia) {
            if (source === imageSource.pageContext) {
                if (this.state.mediaGalleryItems === undefined && this.props.data.mediaLocationsForSelectedVariant.result) {
                    const images = await validateProductImages(
                        this.props.data.mediaLocationsForSelectedVariant.result,
                        this.props.context.actionContext,
                        this.props.config.thumbnailImageSettings ?? this.defaultThumbnailImageSettings
                    );
                    await this._setImages(images);
                } else if (this.state.mediaGalleryItems === undefined && this.props.data.product.result) {
                    const product = this.props.data.product.result;
                    const images = await getValidProductImages(
                        product.RecordId,
                        +this.props.context.request.apiSettings.channelId,
                        this.props.context.actionContext,
                        this.props.config.thumbnailImageSettings ?? this.defaultThumbnailImageSettings,
                        product.productVariant
                    );

                    await this._setImages(images);
                } else {
                    await this._setImages([]);
                }
            }
        }

        reaction(
            () => {
                const product = this.props.data.product.result;
                if (!product) {
                    return null;
                }

                if (!shouldUpdateOnPartialDimensionSelection || !product.productVariant?.RecordId) {
                    return `${product.RecordId}`;
                }

                return `${product.RecordId}-${product.productVariant.RecordId}`;
            },
            async () => {
                const product = this.props.data.product.result;
                let images: IImageData[] = [];
                if (product) {
                    if (this.props.context.app?.config?.OmniChannelMedia) {
                        images = await getProductImages(
                            product.RecordId,
                            this.shouldUseCmsAltText(),
                            +this.props.context.request.apiSettings.channelId,
                            this.props.context.actionContext,
                            product.Name,
                            shouldUpdateOnPartialDimensionSelection ? product.productVariant : undefined
                        );
                    } else {
                        images = await getValidProductImages(
                            product.RecordId,
                            +this.props.context.request.apiSettings.channelId,
                            this.props.context.actionContext,
                            this.props.config.thumbnailImageSettings ?? this.defaultThumbnailImageSettings,
                            shouldUpdateOnPartialDimensionSelection ? product.productVariant : undefined
                        );
                    }
                    await this._setImages(images);
                } else {
                    await this._setImages([]);
                }
            },
            { fireImmediately: true }
        );

        if (source === imageSource.productId && this.props.data.mediaLocations.result) {
            const images = await validateProductImages(
                this.props.data.mediaLocations.result,
                this.props.context.actionContext,
                this.props.config.thumbnailImageSettings ?? this.defaultThumbnailImageSettings
            );
            await this._setImages(images);
        }
    }

    public shouldComponentUpdate(nextProps: IBritaxMediaGalleryProps<IBritaxMediaGalleryData>, nextState: IMediaGalleryState): boolean {
        if (this.state === nextState && this.props.data === nextProps.data) {
            return false;
        }
        return true;
    }

    public render(): JSX.Element {
        const { id, config, resources } = this.props;

        const { className, showPaginationTooltip } = config;

        const isVertical: boolean = config.thumbnailsOrientation === thumbnailsOrientation.vertical;
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- || is required.
        const isFullscreenAllowed: boolean = this.isMobile() || config.allowFullScreen || false;
        const zoomViewMode: string =
            config.imageZoom === imageZoom.inline ? imageZoom.inline : config.imageZoom === imageZoom.container ? imageZoom.container : '';
        const source = this.props.config.imageSource ?? imageSource.pageContext;
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- Site level config can be of any type.
        const emptyPlaceHolderImage = this.props.context.app.config?.placeholderImageName as string;
        if (this.props.data.product.result) {
            this.fallbackImage = getFallbackImageUrl(
                this.props.data.product.result.ItemId,
                this.props.context.request.apiSettings,
                this.props.context.request.app?.config?.OmniChannelMedia
            );
        }
        if (emptyPlaceHolderImage && this.fallbackImage) {
            this.fallbackImage = `${this.fallbackImage},${emptyPlaceHolderImage}`;
        }
        if (this.props.config.skipImageValidation && !this.state.mediaGalleryItems && this.state.isInitialLoad) {
            let images: IImageData[] = [];
            let mediaLocations: MediaLocation[] = [];
            if (source === imageSource.pageContext && this.props.data.mediaLocationsForSelectedVariant.result) {
                mediaLocations = this.props.data.mediaLocationsForSelectedVariant.result;
            } else if (source === imageSource.productId && this.props.data.mediaLocations.result) {
                mediaLocations = this.props.data.mediaLocations.result;
            }
            images = mediaLocations.map(mediaLocation => {
                return {
                    src: mediaLocation.Uri ?? '',
                    altText: getAltText(
                        this.props.context.actionContext.requestContext,
                        this.shouldUseCmsAltText(),
                        this.props.data.product.result?.Name,
                        mediaLocation.AltText
                    )
                };
            });
            const curatedImages = this.props.config.images ?? [];
            this.mediaGalleryImages = [...this._filterMasterImageFromVariant(images), ...curatedImages];
        }
        const mediaGalleryCarouselItems = this._getMediaGalleryItems(isFullscreenAllowed, zoomViewMode);
        const mediaGalleryThumbnailCarouselItems = this._getMediaGalleryThumbnailItems();
        const viewProps: IMediaGalleryViewProps = {
            ...(this.props as IBritaxMediaGalleryProps<IBritaxMediaGalleryData>),
            state: this.state,
            MediaGallery: {
                moduleProps: this.props,
                className: classnames(`ms-media-gallery ${isVertical ? 'vertical' : ''}`, className)
            },
            Modal: isFullscreenAllowed ? this.imageModalSlider(zoomViewMode) : null,
            callbackToggle: this.openModalDialog,
            callbackThumbnailClick: this._generateOnThumbnailClick,
            callbackThumbnailKeyDown: this._generateOnThumbnailKeyDown,
            CarouselProps: {
                tag: Carousel,
                className: 'ms-media-gallery__carousel',
                items: mediaGalleryCarouselItems.items,
                activeIndex: this.state.activeIndex,
                next: this.next,
                previous: this.previous,
                interval: false,
                directionTextPrev: resources.previousScreenshotFlipperText,
                directionTextNext: resources.nextScreenshotFlipperText,
                onIndicatorsClickHandler: this.goToIndex,
                showPaginationTooltip: showPaginationTooltip === true,
                indicatorAriaText: resources.ariaLabelForSlide,
                handleOnExited: this.onExited,
                handleOnExiting: this.onExiting,
                key: mediaGalleryCarouselItems.keys
            } as IComponentNodeProps<ICarouselProps>,
            Thumbnails: {
                ThumbnailsContainerProps: { className: 'ms-media-gallery__thumbnails-container' },
                SingleSlideCarouselComponentProps: {
                    tag: SingleSlideCarousel,
                    className: 'ms-media-gallery__thumbnails',
                    vertical: isVertical,
                    flipperPrevLabel: resources.previousScreenshotFlipperText,
                    flipperNextLabel: resources.nextScreenshotFlipperText,
                    parentId: id,
                    useTabList: true,
                    key: JSON.stringify(mediaGalleryThumbnailCarouselItems.keys)
                } as IComponentNodeProps<ISingleSlideCarouselProps>,
                items: mediaGalleryThumbnailCarouselItems.items
            }
        };

        return this.props.renderView(viewProps) as React.ReactElement;
    }

    /**
     * Zoomed out image on previous/next click.
     */
    public updateZoomedInImage(): void {
        this.setState({ isImageZoomed: false });
    }

    private readonly onExiting = () => {
        this.setState({ animating: true });
    };

    private readonly onExited = () => {
        this.setState({ animating: false });
    };

    /**
     * On click next in carousel.
     */
    private readonly next = (): void => {
        removeInlineZoomStyle();
        if (this.isLastItem() === undefined) {
            return;
        }

        const nextIndex = this.isLastItem() ? 0 : this.state.activeIndex + 1;
        this.goToIndex(nextIndex);

        this.updateZoomedInImage();
    };

    /**
     * On click previous in carousel.
     */
    private readonly previous = (): void => {
        removeInlineZoomStyle();
        const images = this.state.mediaGalleryItems;
        const nextIndex = this.isFirstItem() ? (images ? images.length - 1 : 0) : this.state.activeIndex - 1;
        this.goToIndex(nextIndex);
        this.updateZoomedInImage();
    };

    private readonly goToIndex = (index: number): void => {
        const { autoplay } = this.props.config;
        const currentIndex = this.state.activeIndex;
        const mediaItems = this.state.mediaGalleryItems;
        if (autoplay && mediaItems) {
            if (mediaItems[`${index}`]?.mediaTypeValue === MediaType.Video) {
                this.player.current?.play();
            } else if (mediaItems[`${currentIndex}`].mediaTypeValue === MediaType.Video) {
                this.player.current?.pause();
            }
        }
        this.setState({ activeIndex: index });
    };

    private _getMediaGalleryThumbnailItems(): IMediaGalleryCarouselItems<IMediaGalleryThumbnailItemViewProps> {
        const mediaGalleryItems = this.state.mediaGalleryItems ?? this.mediaGalleryImages;
        const thumbnailImageSettings = this.props.config.thumbnailImageSettings;
        if (thumbnailImageSettings) {
            thumbnailImageSettings.cropFocalRegion = true;
        }

        const hasMediaGalleryItems = ArrayExtensions.hasElements(mediaGalleryItems);

        if (!hasMediaGalleryItems) {
            if (this.state.lastUpdate) {
                const defaultKey = 0;
                return {
                    items: [this._getEmptyThumbnailItem(thumbnailImageSettings, defaultKey, this.state.activeIndex)],
                    keys: ['empty']
                };
            }
            return { items: [], keys: [] };
        }

        return {
            items: [
                // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
                ...mediaGalleryItems!.map((item: IMediaData, index: number) => {
                    if (item.mediaTypeValue === MediaType.Video) {
                        return this.getVideoThumbNailItem(
                            item,
                            thumbnailImageSettings ?? this.defaultThumbnailImageSettings,
                            index,
                            this.state.activeIndex
                        );
                    } else {
                        return this._getThumbnailItem(
                            item,
                            thumbnailImageSettings ?? this.defaultThumbnailImageSettings,
                            index,
                            this.state.activeIndex
                        );
                    }
                })
            ],
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
            keys: [...mediaGalleryItems!.map(item => item.src)]
        };
    }

    private _getMediaGalleryItems(isFullscreenAllowed: boolean, zoomViewMode: string): IMediaGalleryCarouselItems<React.ReactNode> {
        const mediaGalleryItems = this.state.mediaGalleryItems ?? this.mediaGalleryImages;
        const galleryImageSettings = this.props.config.galleryImageSettings;
        if (galleryImageSettings) {
            galleryImageSettings.cropFocalRegion = true;
        }

        const zoomView = isFullscreenAllowed ? 'fullscreen' : zoomViewMode;

        const hasMediaGalleryItems = ArrayExtensions.hasElements(mediaGalleryItems);

        if (!hasMediaGalleryItems) {
            if (this.state.lastUpdate) {
                return { items: [this._renderEmptyImage(galleryImageSettings)], keys: ['empty'] };
            }
            return { items: [], keys: [] };
        }

        return {
            items: [
                // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
                ...mediaGalleryItems!.map((item: IMediaData, index: number) => {
                    return this._renderCarouselItemImageView(
                        zoomView,
                        item,
                        galleryImageSettings ?? this.defaultGalleryImageSettings,
                        index
                    );
                })
            ],
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
            keys: [...mediaGalleryItems!.map(item => item.src)]
        };
    }

    private async _setImages(images?: IMediaData[]): Promise<void> {
        const curatedImage = this.props.config.images || [];
        const mediaToSet: IMediaData[] = this._filterMasterImageFromVariant(images);

        void Promise.all(
            mediaToSet.map(async mediaData => {
                if (mediaData.mediaTypeValue === MediaType.Video && mediaData.src) {
                    try {
                        const url = new URL(mediaData.src);
                        const videoId = url.searchParams.get('CVID');
                        if (videoId) {
                            mediaData.videoData = await getVideoMetadataFromApi(videoId, this.props.context.actionContext);
                        }
                    } catch (error) {
                        if (this.props.telemetry) {
                            this.props.telemetry.debug('Error while fetching videoId');
                        }
                    }
                }
                return mediaData;
            })
        ).then(mediaData => {
            const mediaItems = mediaData.filter(element => {
                return (
                    element.mediaTypeValue !== MediaType.Video ||
                    (element.mediaTypeValue === MediaType.Video && element.videoData !== undefined)
                );
            });

            this.setState({
                mediaGalleryItems: [...mediaItems, ...curatedImage],
                activeIndex: 0,
                lastUpdate: Date.now(),
                shouldUseOptimizedImage: false
            });
        });

        this.setState({
            mediaGalleryItems: [...mediaToSet, ...curatedImage],
            activeIndex: 0,
            lastUpdate: Date.now(),
            shouldUseOptimizedImage: false
        });
    }

    private _setRenderType(): void {
        this.setState({
            isInitialLoad: false
        });
    }

    private _renderCarouselItemImageView(
        zoomView: string,
        media: IMediaData,
        imageSettings: IImageSettings,
        index: number,
        isInPopup: boolean = false
    ): React.ReactNode {
        if (media.mediaTypeValue === MediaType.Video) {
            if (isInPopup || zoomView !== 'fullscreen') {
                return <>{this._renderCarouselItemVideo(media, index, imageSettings)}</>;
            }
            return <>{this._renderVideoThumbnail(media, index, imageSettings)}</>;
        } else {
            if (this.isMobile()) {
                if (isInPopup) {
                    return this._renderImageInContainerOnZoom(media, this._zoomedImageSettings, index);
                }
                return this._renderImageFullScreenOnZoom(media, imageSettings, index);
            }

            switch (zoomView) {
                case 'container': {
                    return this._renderImageInContainerOnZoom(media, this._zoomedImageSettings, index);
                }
                case 'inline': {
                    return this._renderImageInlineOnZoom(media, this._zoomedImageSettings, index);
                }
                case 'fullscreen': {
                    return this._renderImageFullScreenOnZoom(media, imageSettings, index);
                }

                // No default
            }
            return <>{this._getCarouselItem(media, imageSettings, index, isInPopup)}</>;
        }
    }

    private _filterMasterImageFromVariant(images?: IImageData[]): IImageData[] {
        const shouldHidePrimaryImages = this.props.config.shouldHideMasterProductImagesForVariant ?? true;
        let variantImages = images ?? [];
        const isVariantProduct =
            !ObjectExtensions.isNullOrUndefined(this.props.data.product.result?.MasterProductId) ||
            !ObjectExtensions.isNullOrUndefined(this.props.data.product.result?.productVariant);
        const itemId = this.props.data.product.result?.ItemId;
        if (shouldHidePrimaryImages && isVariantProduct && itemId) {
            variantImages = variantImages.filter(item => {
                const isApplicableForChildEntities = item.additionalProperties?.isApplicableForChildEntities;
                return !isApplicableForChildEntities;
            });
            if (!ArrayExtensions.hasElements(variantImages)) {
                variantImages = images ?? [];
            }
        }
        return variantImages;
    }

    private readonly _renderCarouselItemVideo = (mediaData: IMediaData, index: number, imageSettings: IImageSettings): React.ReactNode => {
        const videoData = mediaData.videoData;
        if (videoData) {
            const options: IPlayerOptions = this.props.config as IPlayerOptions;
            const playerResources = this.props.resources;
            const videoMetaData: IVideoMetadata = {};
            videoMetaData.videoId = videoData.id;
            videoMetaData.title = videoData.title;
            videoMetaData.duration = videoData.playTime;
            if (videoData.thumbnail) {
                videoMetaData.posterframeUrl = videoData.thumbnail.src;
            }
            if (videoData._links?.binaryReferences) {
                videoMetaData.videoBinaryReferences = videoData._links.binaryReferences;
            }
            const videoPlayerData: IPlayerData = { options, metaData: videoMetaData };
            const attributes = {
                id: `${this.props.id}__carousel-item__${index}`
            };
            return (
                <Video className='ms-media-gallery__player' editProps={{ key: videoData, requestContext: this.props.context.request }}>
                    <Player
                        ref={this.player}
                        playerData={videoPlayerData}
                        isEditor={this.props.context.request.params.isEditor}
                        playerResources={playerResources}
                        {...attributes}
                    />
                </Video>
            );
        }
        return this._renderEmptyImage(imageSettings);
    };

    private readonly _getCarouselItem = (
        image: IImageData,
        imageSettings: IImageSettings,
        index: number,
        isInPopup: boolean = false
    ): React.ReactNode => (
        <Image
            requestContext={this.props.context.actionContext.requestContext}
            className='ms-media-gallery__item'
            {...image}
            fallBackSrc={index === 0 ? this.fallbackImage : image.src}
            gridSettings={this.props.context.request.gridSettings!}
            imageSettings={imageSettings}
            loadFailureBehavior='hide'
            id={`${this.props.id}__carousel-item__${index}`}
            shouldSkipToMainImage={isInPopup}
            bypassHideOnFailure={index === 0}
        />
    );

    private readonly _getThumbnailItem = (
        image: IImageData,
        imageSettings: IImageSettings,
        index: number,
        modifiedActiveIndex: number
    ): IMediaGalleryThumbnailItemViewProps => {
        const classes = classnames(
            'ms-media-gallery__thumbnail-item',
            modifiedActiveIndex === index ? 'ms-media-gallery__thumbnail-item-active' : ''
        );

        return {
            ThumbnailItemContainerProps: {
                tag: 'li' as NodeTag,
                className: classes,
                role: 'tab',
                tabIndex: 0,
                key: index,
                'aria-label': image.altText,
                'aria-selected': modifiedActiveIndex === index,
                onClick: this._generateOnThumbnailClick(index),
                onKeyDown: this._generateOnThumbnailKeyDown(index)
            },
            Picture: (
                <Image
                    requestContext={this.props.context.actionContext.requestContext}
                    className='ms-media-gallery__thumbnail'
                    {...image}
                    fallBackSrc={index === 0 ? this.fallbackImage : image.src}
                    gridSettings={this.props.context.request.gridSettings!}
                    imageSettings={imageSettings}
                    loadFailureBehavior='hide'
                    imageFallbackOptimize={this.state.shouldUseOptimizedImage}
                    bypassHideOnFailure={index === 0}
                />
            )
        };
    };

    /**
     * Gets the video thumbnail item to display media gallery.
     * @param props - The Media gallery view props from business layer.
     * @returns Return thumbnail view props which will be used to render video.
     */
    private readonly getVideoThumbNailItem = (
        mediaData: IMediaData,
        imageSettings: IImageSettings,
        index: number,
        modifiedActiveIndex: number
    ): IMediaGalleryThumbnailItemViewProps => {
        const classes = classnames(
            'ms-media-gallery__thumbnail-item',
            modifiedActiveIndex === index ? 'ms-media-gallery__thumbnail-item-active' : ''
        );

        const videoData = mediaData.videoData;
        let image: IImageData;
        if (videoData?.thumbnail) {
            image = videoData.thumbnail;
        } else {
            image = {
                src: 'empty'
            };
        }

        return {
            ThumbnailItemContainerProps: {
                tag: 'li' as NodeTag,
                className: classes,
                role: 'tab',
                tabIndex: 0,
                key: index,
                'aria-label': image.altText,
                'aria-selected': modifiedActiveIndex === index,
                onClick: this._generateOnThumbnailClick(index),
                onKeyDown: this._generateOnThumbnailKeyDown(index)
            },
            Picture: (
                <Image
                    requestContext={this.props.context.actionContext.requestContext}
                    className='ms-media-gallery__thumbnail'
                    {...image}
                    fallBackSrc={image.src}
                    gridSettings={this.props.context.request.gridSettings!}
                    imageSettings={imageSettings}
                    loadFailureBehavior='hide'
                    imageFallbackOptimize={this.state.shouldUseOptimizedImage}
                    bypassHideOnFailure={index === 0}
                />
            )
        };
    };

    /**
     * Ref Handler.
     * @param index -Remove item click function.
     * @returns Set inline zoom.
     */
    private readonly _refHandler = (index: number) => (divRef: HTMLDivElement) => {
        this._inlineZoomDivRef.set(index, divRef);
    };

    private readonly _generateOnThumbnailKeyDown = (index: number) => {
        return (event: React.KeyboardEvent) => {
            if (event.which === KeyCodes.Enter || event.which === KeyCodes.Space) {
                event.preventDefault();

                this.goToIndex(index);
            }
        };
    };

    private readonly _generateOnThumbnailClick = (index: number) => {
        return (event: React.MouseEvent<HTMLLIElement>) => {
            event.preventDefault();

            this.goToIndex(index);
        };
    };

    private _renderImageInlineOnZoom(image: IImageData, imageSettings: IImageSettings, index: number): React.ReactNode {
        return (
            <div
                className={`ms-inline-zoom ${this.state.isImageZoomed ? 'zoomed' : ''}`}
                ref={this._refHandler(index)}
                data-scale={this.props.config.dataScale ?? defaultDataScale}
            >
                <Image
                    requestContext={this.props.context.actionContext.requestContext}
                    className='ms-media-gallery__item'
                    {...image}
                    fallBackSrc={index === 0 ? this.fallbackImage : image.src}
                    gridSettings={this.props.context.request.gridSettings!}
                    imageSettings={imageSettings}
                    loadFailureBehavior='hide'
                    onClick={this._onInlineImageClick}
                    onMouseOver={(this.state.isImageZoomed && this._inlineZoomImageOnHover) || undefined}
                    id={`${this.props.id}__carousel-item__${index}`}
                    shouldSkipToMainImage
                    imageFallbackOptimize={this.state.shouldUseOptimizedImage && !this.props.config.allowFullScreen}
                    bypassHideOnFailure={index === 0}
                />
                <Image
                    role='presentation'
                    requestContext={this.props.context.actionContext.requestContext}
                    className='ms-inline-zoom__zoomedImg'
                    {...image}
                    fallBackSrc={index === 0 ? this.fallbackImage : image.src}
                    onClick={this._onImageMouseOut}
                    gridSettings={this.props.context.request.gridSettings!}
                    imageSettings={imageSettings}
                    loadFailureBehavior='hide'
                    id={`${this.props.id}__zoom__${index}`}
                    onMouseMove={(this.state.isImageZoomed && inlineZoomImageOnMouseMove) || undefined}
                    shouldSkipToMainImage
                    imageFallbackOptimize={this.state.shouldUseOptimizedImage && !this.props.config.allowFullScreen}
                    bypassHideOnFailure={index === 0}
                />
            </div>
        );
    }

    private _inlineZoomImageOnHover(event: React.MouseEvent<HTMLImageElement>): void {
        inlineZoomImageOnHover(event, this.props.config.dataScale ?? String(defaultDataScale));
    }

    private _handleMobileViewZoomedImageClick(event: React.MouseEvent<HTMLImageElement>) {
        const target = this.state.shouldUseOptimizedImage ? (event.target as HTMLImageElement) : event.currentTarget;
        const mobileZoomedInImageClassName = 'msc-mobile-zoomed-in';
        if (!this.state.isMobileImageZoomed) {
            const bounds = target.getBoundingClientRect();
            const dataScale = Number(this.props.config.dataScale ?? defaultDataScale);

            const positionX = event.clientX - bounds.left;
            const positionY = event.clientY - bounds.top;
            const scaledPositionX = positionX * dataScale;
            const scaledPositionY = positionY * dataScale;

            target.style.transform = `scale(${dataScale})`;
            target.classList.add(mobileZoomedInImageClassName);
            target.parentElement!.style.overflow = 'auto';
            target.parentElement!.scrollTo(scaledPositionX - positionX, scaledPositionY - positionY);
            this.setState({
                isMobileImageZoomed: true
            });
        } else {
            target.style.transform = '';
            target.classList.remove(mobileZoomedInImageClassName);
            target.parentElement!.style.overflow = '';
            this.setState({
                isMobileImageZoomed: false
            });
        }
    }

    private _onInlineImageClick(event: React.MouseEvent<HTMLImageElement>): void {
        if (window.innerWidth <= 768) {
            // $msv-breakpoint-m
            this._handleMobileViewZoomedImageClick(event);
            return;
        }
        inlineZoomInitClick(event, this.props.config.dataScale ?? String(defaultDataScale));
        this.setState({
            isImageZoomed: true
        });
    }

    private _onImageMouseOut(event: React.MouseEvent<HTMLImageElement, MouseEvent>): void {
        inlineZoomImageOnMouseOut(event);
        this.setState({
            isImageZoomed: false
        });
    }

    private _onContainerZoomImageClick(event: React.MouseEvent<HTMLImageElement>): void {
        if (window.innerWidth <= 768) {
            // $msv-breakpoint-m
            this._handleMobileViewZoomedImageClick(event);
            return;
        }

        onContainerZoomInit(event);

        this.setState({
            isImageZoomed: true
        });
    }

    private _onMouseOutLensContainer(event: React.MouseEvent<HTMLImageElement>): void {
        onMouseOutLensContainer(event);

        this.setState({
            isImageZoomed: false
        });
    }

    private _renderImageInContainerOnZoom(image: IImageData, imageSettings: IImageSettings, index: number): React.ReactNode {
        return (
            <div className='ms-containerZoom__container'>
                <div
                    data-scale={this.props.config.dataScale ?? defaultDataScale}
                    className='ms-containerZoom__zoom-lens'
                    role='presentation'
                    onMouseOut={this._onMouseOutLensContainer}
                    onClick={this._onMouseOutLensContainer}
                    onMouseMove={(this.state.isImageZoomed && onMouseMoveLensContainer) || undefined}
                />
                <Image
                    requestContext={this.props.context.actionContext.requestContext}
                    className='ms-media-gallery__item'
                    {...image}
                    fallBackSrc={index === 0 ? this.fallbackImage : image.src}
                    gridSettings={this.props.context.request.gridSettings!}
                    onClick={this._onContainerZoomImageClick}
                    imageSettings={imageSettings}
                    loadFailureBehavior='hide'
                    onMouseOver={(this.state.isImageZoomed && onMouseOverImageContainer) || undefined}
                    id={`${this.props.id}__carousel-item__${index}`}
                    shouldSkipToMainImage
                    imageFallbackOptimize={this.state.shouldUseOptimizedImage && !this.props.config.allowFullScreen}
                    bypassHideOnFailure={index === 0}
                />
            </div>
        );
    }

    private _renderImageFullScreenOnZoom(image: IImageData, imageSettings: IImageSettings, index: number): React.ReactNode {
        return (
            <div className='ms-fullscreen-section'>
                <Image
                    requestContext={this.props.context.actionContext.requestContext}
                    className='ms-media-gallery__item'
                    {...image}
                    fallBackSrc={index === 0 ? this.fallbackImage : image.src}
                    gridSettings={this.props.context.request.gridSettings!}
                    imageSettings={imageSettings}
                    loadFailureBehavior='hide'
                    imageFallbackOptimize={this.state.shouldUseOptimizedImage}
                    bypassHideOnFailure={index === 0}
                />
                <div className='ms-fullscreen-section__overlay'>
                    {}
                    <a
                        href='javascript:void(0);'
                        title={this.props.resources.fullScreenTitleText}
                        role='button'
                        ref={this.fullScreenOverlayButtonRef}
                        onClick={this.openModalDialog}
                        className='ms-fullscreen-section__magnifying-glass-icon'
                    />
                </div>
            </div>
        );
    }

    /**
     * Render the video thumbnail in full screen.
     * @param mediaData - Media Data.
     * @param index - Index
     * @param imageSettings - Image settings.
     * @returns - JSX.Element
     */
    private readonly _renderVideoThumbnail = (mediaData: IMediaData, index: number, imageSettings: IImageSettings): React.ReactNode => {
        const videoData = mediaData.videoData;
        let image: IImageData;
        if (videoData?.thumbnail) {
            image = videoData.thumbnail;
        } else {
            image = {
                src: 'empty'
            };
        }

        return (
            <div className='ms-fullscreen-section'>
                <Image
                    requestContext={this.props.context.actionContext.requestContext}
                    className='ms-fullscreen-section__video'
                    {...image}
                    fallBackSrc={image.src}
                    gridSettings={this.props.context.request.gridSettings!}
                    imageSettings={imageSettings}
                    loadFailureBehavior='hide'
                    imageFallbackOptimize={this.state.shouldUseOptimizedImage}
                    bypassHideOnFailure={index === 0}
                />
                <div className='ms-fullscreen-section__video-overlay'>
                    {}
                    <button
                        title={this.props.resources.fullScreenTitleText}
                        role='button'
                        ref={this.fullScreenOverlayButtonRef}
                        onClick={this.openModalDialog}
                        className='ms-fullscreen-section__video-play-icon'
                    />
                </div>
            </div>
        );
    };

    private _mapProductToImageData(product: SimpleProduct): IImageData {
        return {
            src: product.PrimaryImageUrl ?? ''
        };
    }

    private _renderEmptyImage(imageSettings: IImageSettings | undefined): React.ReactNode {
        return (
            <div className='ms-media-gallery__item'>
                <Image
                    requestContext={this.props.context.actionContext.requestContext}
                    className='ms-media-gallery__item__image'
                    src='empty'
                    gridSettings={this.props.context.request.gridSettings!}
                    imageSettings={imageSettings ?? this.defaultGalleryImageSettings}
                    loadFailureBehavior='empty'
                />
            </div>
        );
    }

    private _getEmptyThumbnailItem(
        imageSettings: IImageSettings | undefined,
        index: number,
        modifiedActiveIndex: number
    ): IMediaGalleryThumbnailItemViewProps {
        const classes = classnames(
            'ms-media-gallery__thumbnail-item',
            modifiedActiveIndex === index ? 'ms-media-gallery__thumbnail-item-active' : ''
        );
        return {
            ThumbnailItemContainerProps: {
                tag: 'li' as NodeTag,
                className: classes,
                role: 'tab',
                tabIndex: 0,
                key: 'empty',
                'aria-label': '',
                'aria-selected': modifiedActiveIndex === index,
                onClick: this._generateOnThumbnailClick(index),
                onKeyDown: this._generateOnThumbnailKeyDown(index)
            },
            Picture: (
                <Image
                    requestContext={this.props.context.actionContext.requestContext}
                    className='ms-media-gallery__thumbnail-item__image'
                    src='empty'
                    gridSettings={this.props.context.request.gridSettings!}
                    imageSettings={imageSettings ?? this.defaultThumbnailImageSettings}
                    loadFailureBehavior='empty'
                />
            )
        };
    }

    private readonly isFirstItem = () => this.state.activeIndex === 0;

    private readonly isLastItem = () => {
        const images = this.state.mediaGalleryItems;
        return images && this.state.activeIndex === images.length - 1;
    };

    private readonly openModalDialog = (): void => {
        this._toggleModal();
    };

    private _toggleModal(): void {
        if (this.state.modalIsOpen) {
            this.fullScreenOverlayButtonRef.current?.focus();
            const parentDiv = this._inlineZoomDivRef.get(this.state.activeIndex);
            if (parentDiv && parentDiv.children && parentDiv.children.length >= 2) {
                const image = parentDiv.children[1].querySelector('img');
                if (image) {
                    image.removeAttribute('style');
                }
            }
            this.setState({
                isImageZoomed: false
            });
        }
        this.setState({
            isImageZoomed: false,
            modalIsOpen: !this.state.modalIsOpen
        });
        removeInlineZoomStyle();
        removeContainerZoomStyle();
    }

    private readonly imageModalSlider = (ZoomView: string): React.ReactElement => {
        const {
            data: {
                product: { result: product }
            },
            resources
        } = this.props;
        let mediaGalleryItems = this.state.mediaGalleryItems;

        if (!mediaGalleryItems && product) {
            mediaGalleryItems = [this._mapProductToImageData(product)];
        }
        const carouselprops = {
            tag: Carousel,
            className: 'ms-media-gallery__carousel',
            items:
                mediaGalleryItems &&
                mediaGalleryItems.map((item: IImageData, index: number) =>
                    this._renderCarouselItemImageView(ZoomView, item, this._zoomedImageSettings, index, true)
                ),
            activeIndex: this.state.activeIndex,
            next: this.next,
            previous: this.previous,
            interval: false,
            directionTextPrev: resources.previousScreenshotFlipperText,
            directionTextNext: resources.nextScreenshotFlipperText,
            onIndicatorsClickHandler: this.goToIndex,
            showPaginationTooltip: true,
            hideIndicator: false,
            keyboard: false,
            handleOnExited: this.onExited,
            handleOnExiting: this.onExiting,
            isDisabledFunctionality: this.state.isMobileImageZoomed
        } as IComponentNodeProps<ICarouselProps>;
        const carousel = <Carousel {...carouselprops} />;

        const imageModalSliderProps: IModalViewProps = {
            modalIsOpen: this.state.modalIsOpen,
            ontoggle: this._toggleModal,
            galleryCarousel: carousel,
            classNames: classnames('ms-media-gallery__modal', 'msc-modal-input-required')
        };
        return ImagefullView(imageModalSliderProps) as React.ReactElement;
    };

    private shouldUseCmsAltText = (): boolean => {
        if (this.props.config.altTextSource === 'product') {
            return false;
        } else {
            return true;
        }
    };
}

export default MediaGallery;
